import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api, isApiError } from '@/lib/api';

export type ScheduleKind = 'weekly_template' | 'date_override';

export interface StaffSchedule {
  id: string;
  staffUserId: string;
  branchId: string;
  kind: ScheduleKind;
  weekday: number | null;
  date: string | null;
  startMin: number;
  endMin: number;
}

export interface ScheduleDoctor {
  id: string;
  fullName: string;
}

export interface MySchedule {
  templates: StaffSchedule[];
  overrides: StaffSchedule[];
  /** RL-1884: my own requests and the decisions, newest first. */
  requests?: ScheduleChangeRequest[];
}

export type ScheduleRequestStatus = 'pending' | 'approved' | 'declined';

export interface ScheduleChangeRequest {
  id: string;
  staffUserId: string;
  branchId: string;
  kind: string;
  date: string;
  text: string;
  status: ScheduleRequestStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  /** RL-1883: the calendar leave an approved doctor leave request added. */
  calendarEventId?: string | null;
}

export interface SubmitScheduleRequestInput {
  branchId: string;
  kind: string;
  date: string;
  text: string;
}

export interface ManagerSchedules {
  schedules: StaffSchedule[];
  requests: ScheduleChangeRequest[];
}

export interface DecideScheduleRequestInput {
  id: string;
  status: Exclude<ScheduleRequestStatus, 'pending'>;
}

export interface SaveScheduleInput {
  staffUserId: string;
  branchId: string;
  kind: ScheduleKind;
  weekday: number | null;
  date: string | null;
  startMin: number;
  endMin: number;
}

export interface UpdateScheduleInput extends SaveScheduleInput {
  id: string;
}

const scheduleKeys = {
  all: ['schedules'] as const,
  mine: ['schedules', 'mine'] as const,
  manager: ['schedules', 'manager'] as const,
  doctors: ['schedules', 'doctors'] as const,
};

export function useMySchedule(enabled = true) {
  return useQuery({
    queryKey: scheduleKeys.mine,
    queryFn: ({ signal }) =>
      api.get<MySchedule>('/schedules/me', { signal }).then((r) => r.data),
    enabled,
  });
}

export function useSubmitScheduleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitScheduleRequestInput) =>
      api
        .post<ScheduleChangeRequest>('/schedule-change-requests', input)
        .then((r) => r.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: scheduleKeys.all }),
  });
}

export function useManagerSchedules() {
  const branch = useBranch().selected?.id ?? 'all';
  return useQuery({
    queryKey: [...scheduleKeys.manager, branch],
    queryFn: async ({ signal }): Promise<ManagerSchedules> => {
      const [schedules, requests] = await Promise.all([
        api.get<StaffSchedule[]>('/admin/schedules', { signal }),
        api.get<ScheduleChangeRequest[]>('/admin/schedule-change-requests', {
          signal,
        }),
      ]);
      return { schedules: schedules.data, requests: requests.data };
    },
  });
}

export function useScheduleDoctors() {
  return useQuery({
    queryKey: scheduleKeys.doctors,
    queryFn: ({ signal }) =>
      api
        .get<ScheduleDoctor[]>('/doctors', { signal })
        .then((response) => response.data),
    staleTime: 30_000,
  });
}

function isStaleScheduleError(error: unknown) {
  return isApiError(error) && error.code === 'SCHEDULE_NOT_FOUND';
}

function isStaleRequestError(error: unknown) {
  return isApiError(error) && error.code === 'SCHEDULE_REQUEST_NOT_FOUND';
}

export function useScheduleMutations() {
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: scheduleKeys.manager });
  const refreshStale = (error: unknown) => {
    if (isStaleScheduleError(error)) refresh();
  };

  return {
    create: useMutation({
      mutationFn: (input: SaveScheduleInput) =>
        api
          .post<StaffSchedule>('/admin/schedules', input)
          .then((response) => response.data),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: UpdateScheduleInput) =>
        api
          .patch<StaffSchedule>(`/admin/schedules/${id}`, input)
          .then((response) => response.data),
      onSuccess: refresh,
      onError: refreshStale,
    }),
    remove: useMutation({
      mutationFn: (id: string) =>
        api
          .del<{ id: string; removed: boolean }>(`/admin/schedules/${id}`)
          .then((response) => response.data),
      onSuccess: refresh,
      onError: refreshStale,
    }),
  };
}

export function useDecideScheduleRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: DecideScheduleRequestInput) =>
      api
        .patch<ScheduleChangeRequest>(
          `/admin/schedule-change-requests/${id}/decision`,
          { status },
        )
        .then((r) => r.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: scheduleKeys.manager }),
    onError: (error) => {
      if (isStaleRequestError(error)) {
        void queryClient.invalidateQueries({ queryKey: scheduleKeys.manager });
      }
    },
  });
}
